import {
  CSSProperties,
  HTMLAttributes,
  useEffect,
  useRef,
  useState
} from 'react';
import { TextArea as InformedTextArea, useFormApi } from 'informed';
import { useReduxState } from 'hooks';

export interface TextAreaProps extends HTMLAttributes<HTMLTextAreaElement> {
  autoGrow?: boolean;
  autoGrowUpdateProp?: boolean;
  darkMode?: boolean;
  disabled?: boolean;
  error?: string;
  field?: string;
  id?: string;
  infoMessage?: string;
  initialValue?: any;
  maxLength?: number;
  minHeight?: number;
  onValueChange?: (value: string) => void;
  placeholder?: string;
  preventNewLine?: boolean;
  rows?: number;
  size?: string;
  style?: CSSProperties;
  textareaContainerRef?: (el: HTMLDivElement) => void;
  validate?: (value: any, values?: any) => undefined | string;
  validateOnChange?: boolean;
}

const TextArea = ({
  autoGrow,
  autoGrowUpdateProp,
  darkMode,
  error,
  field,
  id,
  infoMessage,
  maxLength,
  minHeight,
  onChange,
  onValueChange,
  placeholder,
  preventNewLine,
  rows = 5,
  size = 'medium',
  style,
  textareaContainerRef,
  ...params
}: TextAreaProps) => {
  const { messages, theme } = useReduxState();

  const { setValue: setFormValue } = useFormApi();

  const [triggerAutoGrow, setTriggerAutoGrow] = useState(false);
  const [value, setValue] = useState('');

  const containerRef = useRef<HTMLDivElement>(undefined);

  useEffect(() => {
    handleAutoGrow();
    const textarea = containerRef?.current?.querySelector('textarea');
    setValue(textarea?.value || '');
  }, []);

  useEffect(() => {
    if (triggerAutoGrow) {
      handleAutoGrow();
      setTriggerAutoGrow(false);
    }
  });

  useEffect(() => {
    handleAutoGrow();
  }, [autoGrowUpdateProp]);

  const handleAutoGrow = () => {
    if (!autoGrow) return;

    const textarea = containerRef?.current?.querySelector('textarea');
    if (!textarea) return;

    textarea.style.height = '';

    const referenceHeight = minHeight || 38;
    const scrollHeight = textarea.scrollHeight + 2;
    textarea.style.height = `${Math.max(scrollHeight, referenceHeight)}px`;
  };

  const handleChange = (ev) => {
    if (preventNewLine) {
      const sanitizedValue = (ev.currentTarget?.value || '').replace(
        /(\r\n|\n|\r)/g,
        ''
      );
      setFormValue(field, sanitizedValue);
    }
    if (onChange) onChange(ev);
  };

  const handleValueChange = (value) => {
    setTriggerAutoGrow(true);
    setValue(value);
    if (onValueChange) onValueChange(value);
  };

  let color = theme.textColor;
  let backgroundColor = '#fff';
  let backgroundColorDisabled = theme.lightGreyLighter;
  let borderColor = theme.lightGreyDark;
  let borderColorActive = '#a1a8aa';
  let errorColor = theme.dangerColor;
  let infoColor = theme.textColorLighter;
  let placeholderColor = '#999';
  if (darkMode) {
    color = '#fff';
    backgroundColor = 'rgba(0, 0, 0, 0.2)';
    backgroundColorDisabled = 'rgba(255, 255, 255, 0.2)';
    borderColor = 'rgba(255, 255, 255, 0.3)';
    borderColorActive = 'rgba(255, 255, 255, 0.6)';
    errorColor = theme.dangerColor;
    infoColor = '#ddd';
    placeholderColor = '#ccc';
  }

  return (
    <div
      className="textarea"
      ref={(el) => {
        containerRef.current = el;
        if (textareaContainerRef) textareaContainerRef(el);
      }}
    >
      <InformedTextArea
        {...params}
        field={field}
        id={id}
        maxLength={maxLength}
        onChange={handleChange}
        onValueChange={handleValueChange}
        placeholder={placeholder}
        // @ts-ignore
        rows={rows}
        style={{
          fontSize: {
            small: '1.4rem',
            medium: '1.5rem',
            large: '1.6rem',
            huge: '1.8rem'
          }[size],
          ...style
        }}
      />

      {maxLength ? (
        <div className="character-limit">
          {value ? maxLength - value.length : maxLength}
          {` ${messages.commonCharactersRemaining}`}
        </div>
      ) : null}

      {error ? (
        <div
          className="error-message"
          style={{
            fontSize: {
              small: '1.2rem',
              medium: '1.3rem',
              large: '1.4rem',
              huge: '1.6rem'
            }[size]
          }}
        >
          {error}
        </div>
      ) : null}

      {infoMessage ? <div className="info-message">{infoMessage}</div> : null}

      <style jsx>{`
        .textarea {
          position: relative;
        }
        .textarea > :global(textarea) {
          background-color: ${backgroundColor};
          border: 1px solid ${error ? errorColor : borderColor};
          border-radius: ${theme.inputBorderRadius};
          color: ${color};
          font-family: inherit;
          font-size: 1.5rem;
          outline: 0;
          padding: 8px 10px;
          resize: none;
          transition: border-color 0.2s;
          width: 100%;
        }
        .textarea > :global(textarea:focus) {
          border-color: ${error ? errorColor : borderColorActive};
        }
        .textarea > :global(textarea:disabled) {
          background-color: ${backgroundColorDisabled};
          color: ${color};
          opacity: 0.7;
        }
        .textarea > :global(textarea::-webkit-input-placeholder) {
          color: ${placeholderColor};
          opacity: 1;
        }
        .textarea > :global(textarea::-moz-placeholder) {
          color: ${placeholderColor};
          opacity: 1;
        }
        .textarea > :global(textarea::-ms-input-placeholder) {
          color: ${placeholderColor};
          opacity: 1;
        }
        .textarea > :global(textarea:-ms-input-placeholder) {
          color: ${placeholderColor};
          opacity: 1;
        }
        .character-limit {
          color: ${infoColor};
          font-size: 1.3rem;
          font-weight: 400;
          padding: 3px;
        }
        .error-message {
          color: ${errorColor};
          font-size: 1.3rem;
          font-weight: 400;
          padding: 3px;
        }
        .info-message {
          color: ${infoColor};
          font-size: 1.3rem;
          font-weight: 400;
          padding: 3px;
        }
      `}</style>
    </div>
  );
};

export default TextArea;
